import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import { ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [resetEmail, setResetEmail] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(formData.email, formData.password);
    setLoading(false);
    if (result.success) {
      toast({ title: "Inicio de sesión exitoso", description: "Bienvenido a CERTILEX" });
      navigate('/dashboard');
    } else {
      toast({ title: "Error", description: result.error, variant: "destructive" });
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) return;
    setResetLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail, password: '' }),
      });
      if (res.ok) {
        toast({ title: "Solicitud enviada", description: "Si el email está registrado, recibirá una nueva contraseña por correo." });
        setShowReset(false);
        setResetEmail('');
      }
    } catch {
      toast({ title: "Error", description: "No se pudo procesar la solicitud", variant: "destructive" });
    }
    setResetLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4" style={{ background: '#F6F5F0' }}>
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center mb-6" style={{ color: '#0B1D3A', fontWeight: 500 }}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al inicio
        </Link>

        <Card style={{ borderColor: '#E2DDD3' }}>
          <CardHeader className="text-center">
            <CardTitle className="text-3xl" style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#0B1D3A' }}>
              {showReset ? 'Regenerar Contraseña' : 'Iniciar Sesión'}
            </CardTitle>
            <CardDescription>
              {showReset ? 'Introduzca su email para recibir una nueva contraseña' : 'Acceda a su cuenta de CERTILEX'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!showReset ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} placeholder="juan@ejemplo.com" data-testid="login-email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Contraseña</Label>
                  <Input id="password" name="password" type="password" required value={formData.password} onChange={handleChange} placeholder="Su contraseña" data-testid="login-password" />
                </div>
                <Button type="submit" className="w-full" style={{ background: '#C5A55A', color: '#0B1D3A', fontWeight: 700 }} disabled={loading} data-testid="login-submit-btn">
                  {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                </Button>
                <button type="button" onClick={() => setShowReset(true)} className="w-full text-center text-sm hover:underline" style={{ color: '#C5A55A', background: 'none', border: 'none', cursor: 'pointer' }} data-testid="forgot-password-btn">
                  ¿Olvidó su contraseña? Solicitar nueva
                </button>
                <p className="text-center text-sm text-gray-600">
                  ¿No tiene una cuenta?{' '}
                  <Link to="/register" style={{ color: '#C5A55A', fontWeight: 600 }} className="hover:underline">Registro Gratuito</Link>
                </p>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="resetEmail">Email registrado</Label>
                  <Input id="resetEmail" type="email" required value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} placeholder="juan@ejemplo.com" data-testid="reset-email" />
                </div>
                <Button type="submit" className="w-full" style={{ background: '#C5A55A', color: '#0B1D3A', fontWeight: 700 }} disabled={resetLoading} data-testid="reset-submit-btn">
                  {resetLoading ? 'Enviando...' : 'Enviar nueva contraseña'}
                </Button>
                <button type="button" onClick={() => setShowReset(false)} className="w-full text-center text-sm hover:underline" style={{ color: '#4A4A5A', background: 'none', border: 'none', cursor: 'pointer' }}>
                  Volver a Iniciar Sesión
                </button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
